






































import { defineComponent, computed, ref, watch } from '@nuxtjs/composition-api';
import useWindowResize from '~/hooks/useResizeWindow';
import { debounce } from 'lodash';

export default defineComponent({
  name: 'PriceRangeSlider',
  props: {
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100000
    },
    minValue: {
      type: Number,
      required: true
    },
    maxValue: {
      type: Number,
      required: true
    }
  },
  emits: ['update:slider'],
  setup(props, { emit }) {
    const localMinValue = ref(props.minValue);
    const localMaxValue = ref(props.maxValue);

    const { isDesktop } = useWindowResize();

    // Automatically switch values if they are not in order during initialization
    if (props.minValue >= props.maxValue) {
      localMinValue.value = props.maxValue - 1;
      localMaxValue.value = props.maxValue;
    }

    const minPosition = computed(
      () => ((localMinValue.value - props.min) / (props.max - props.min)) * 100
    );
    const maxPosition = computed(
      () => ((localMaxValue.value - props.min) / (props.max - props.min)) * 100
    );

    const updateSliderRange = debounce(() => {
      emit('update:slider', {
        min: localMinValue.value,
        max: localMaxValue.value
      });
    }, 200);

    const handleTrackClick = (event: MouseEvent) => {
      const track = event.currentTarget as HTMLElement;
      const rect = track.getBoundingClientRect();
      const clickPosition = (event.clientX - rect.left) / rect.width;

      // Map the click position to the actual range
      const clickedValue = Math.round(
        (props.min + clickPosition) * (props.max - props.min)
      );

      const minDiff = Math.abs(clickedValue - localMinValue.value);
      const maxDiff = Math.abs(clickedValue - localMaxValue.value);

      // Update the closest value (min or max)
      if (minDiff < maxDiff) {
        localMinValue.value = Math.max(
          props.min,
          Math.min(clickedValue, localMaxValue.value)
        );
      } else {
        localMaxValue.value = Math.min(
          props.max,
          Math.max(clickedValue, localMinValue.value)
        );
      }
      updateSliderRange();
    };

    watch(
      () => props.minValue,
      (newVal) => {
        if (newVal >= localMaxValue.value) {
          const temp = newVal;
          localMinValue.value = localMaxValue.value - 1;
          localMaxValue.value = temp + 1;
        } else {
          localMinValue.value = newVal;
        }
      }
    );

    watch(
      () => props.maxValue,
      (newVal) => {
        if (newVal <= localMinValue.value) {
          const temp = newVal;
          localMaxValue.value = localMinValue.value + 1;
          localMinValue.value = temp - 1;
        } else {
          localMaxValue.value = newVal;
        }
      }
    );

    return {
      isDesktop,
      localMaxValue,
      localMinValue,
      maxPosition,
      minPosition,
      updateSliderRange,
      handleTrackClick
    };
  }
});
